import {Controller} from "stimulus"

// reward-form: drives the in-navigator survey form attached to a reward,
// training, or journey. Wired up via data-controller="reward-form" on the
// container that the rewards/form.html template renders.
//
// We use a Stimulus controller (rather than an inline <script>) because the
// step navigator preserves the form step's HTML via innerHTML round-trips when
// the user navigates to a resource step and back. That round-trip drops live
// event listeners; Stimulus' connect() re-fires on the rebuilt element, so
// Submit/Retake/condition listeners are reattached automatically.
export default class extends Controller {
  connect() {
    const container = this.element
    const fieldsRoot = container.querySelector("#reward-form-fields")
    const errorEl = container.querySelector("#reward-form-error")
    const submitBtn = container.querySelector("#reward-form-submit")
    const retakeBtn = container.querySelector("#reward-form-retake")
    if (!container || !fieldsRoot || !submitBtn) return

    let schema
    try {
      schema = JSON.parse(container.getAttribute("data-form-schema") || "{}")
    } catch (e) {
      console.error("Bad form schema JSON", e)
      return
    }
    const fields = (schema && schema.fields) || []

    let existingAnswers = null
    try {
      const raw = container.getAttribute("data-form-existing-answers")
      if (raw) existingAnswers = JSON.parse(raw)
    } catch (e) {
      /* ignore */
    }

    const alreadySubmitted = container.getAttribute("data-form-already-submitted") === "true"

    function el(tag, attrs, children) {
      const node = document.createElement(tag)
      Object.keys(attrs || {}).forEach((k) => {
        if (k === "class") node.className = attrs[k]
        else node.setAttribute(k, attrs[k])
      })
      ;(children || []).forEach((c) => {
        node.appendChild(typeof c === "string" ? document.createTextNode(c) : c)
      })
      return node
    }

    function labelFor(field) {
      // Google-Forms-style label: 16px regular weight, generous bottom spacing
      // so the input has visual room. Required indicator is the bright-red asterisk.
      const label = el("label", {class: "block text-sm font-vs-regular text-neutral-1 mb-3 leading-snug", for: "rf-" + field.id}, [field.label || ""])
      if (field.required) label.appendChild(el("span", {class: "text-red-600 ml-1"}, ["*"]))
      return label
    }

    // Shared input styling — 16px regular text, clearly bordered, with
    // comfortable padding so the form reads like a real survey instead of a
    // tight admin form.
    const INPUT_CLASS = "input w-full text-sm py-3 px-4 border-2 border-neutral-17 rounded focus:border-primary-1 focus:outline-none"

    function buildField(field) {
      const wrap = el("div", {"data-field-id": field.id, "class": "block"})
      wrap.appendChild(labelFor(field))
      let input
      switch (field.type) {
        case "number":
          input = el("input", {type: "number", id: "rf-" + field.id, name: field.id, class: INPUT_CLASS})
          break
        case "date":
          input = el("input", {type: "date", id: "rf-" + field.id, name: field.id, class: INPUT_CLASS})
          break
        case "range": {
          input = el("div", {class: "flex items-center gap-3"})
          const sliderWrap = el("div", {class: "flex-1 relative"})
          const rangeInput = el("input", {
            type: "range",
            id: "rf-" + field.id,
            name: field.id,
            min: String(field.min || 1),
            max: String(field.max || 5),
            class: "w-full"
          })
          const bubble = el("span", {
            class: "pointer-events-none absolute top-full mt-3 px-2 py-1 rounded bg-primary-1 text-white text-body-sm-regular shadow opacity-0 transition-opacity duration-150",
            style: "transform: translateX(-50%);"
          })
          // Small upward-pointing arrow on top of the bubble.
          const bubbleArrow = el("span", {
            class: "absolute bg-primary-1",
            style: "top: 0; left: 50%; width: 0.5rem; height: 0.5rem; transform: translate(-50%, -50%) rotate(45deg);"
          })
          bubble.appendChild(bubbleArrow)
          ;((inputEl, bubbleEl) => {
            function updateBubble() {
              const minV = Number(inputEl.min)
              const maxV = Number(inputEl.max)
              const val = Number(inputEl.value)
              const pct = maxV - minV > 0 ? (val - minV) / (maxV - minV) : 0
              bubbleEl.textContent = String(val)
              // Center over the thumb: 0% at left edge, 100% at right edge.
              // Slight offset to compensate for thumb width.
              bubbleEl.style.left = "calc(" + pct * 100 + "% + " + (0.5 - pct) * 14 + "px)"
            }
            function show() {
              bubbleEl.style.opacity = "1"
            }
            function hide() {
              bubbleEl.style.opacity = "0"
            }
            inputEl.addEventListener("input", () => {
              updateBubble()
              show()
            })
            inputEl.addEventListener("pointerdown", () => {
              updateBubble()
              show()
            })
            inputEl.addEventListener("focus", () => {
              updateBubble()
              show()
            })
            inputEl.addEventListener("pointerup", hide)
            inputEl.addEventListener("blur", hide)
            updateBubble()
          })(rangeInput, bubble)
          sliderWrap.appendChild(rangeInput)
          sliderWrap.appendChild(bubble)
          input.appendChild(el("span", {class: "text-body-sm-regular text-neutral-3"}, [String(field.min || 1)]))
          input.appendChild(sliderWrap)
          input.appendChild(el("span", {class: "text-body-sm-regular text-neutral-3"}, [String(field.max || 5)]))
          break
        }
        case "multiple_choice":
          if (field.display === "dropdown") {
            input = el("select", {id: "rf-" + field.id, name: field.id, class: INPUT_CLASS})
            input.appendChild(el("option", {value: ""}, ["Select…"]))
            ;(field.options || []).forEach((opt) => {
              input.appendChild(el("option", {value: opt}, [opt]))
            })
          } else {
            input = el("div", {class: "grid gap-y-4 mt-2"})
            ;(field.options || []).forEach((opt) => {
              const lbl = el("label", {class: "flex items-center gap-3 text-sm text-neutral-1 cursor-pointer leading-snug"})
              const inp = el("input", {
                type: field.display === "checkbox" ? "checkbox" : "radio",
                name: field.id,
                value: opt,
                class: "h-5 w-5 accent-primary-1"
              })
              lbl.appendChild(inp)
              lbl.appendChild(document.createTextNode(opt))
              input.appendChild(lbl)
            })
          }
          break
        default:
          // Schema currently restricts type to text/number/date/range/multiple_choice
          // (see banzai.banzai-forms.schema/Field), so this branch only handles
          // "text". If the schema later adds e.g. "textarea", build the matching
          // element here — falling through to <input type="text"> would silently
          // collapse multiline content to a single line.
          input = el("input", {type: "text", id: "rf-" + field.id, name: field.id, class: INPUT_CLASS})
      }
      if (field.required && (input.tagName === "INPUT" || input.tagName === "SELECT")) {
        input.setAttribute("required", "required")
      }
      wrap.appendChild(input)
      return wrap
    }

    function preloadValues(answers) {
      if (!answers) return
      fields.forEach((field) => {
        const val = answers[field.id]
        if (val == null) return
        if (field.type === "multiple_choice" && field.display === "checkbox") {
          const arr = Array.isArray(val) ? val : [val]
          arr.forEach((v) => {
            const box = fieldsRoot.querySelector('input[name="' + field.id + '"][value="' + CSS.escape(String(v)) + '"]')
            if (box) box.checked = true
          })
        } else if (field.type === "multiple_choice" && field.display === "radio") {
          const radio = fieldsRoot.querySelector('input[name="' + field.id + '"][value="' + CSS.escape(String(val)) + '"]')
          if (radio) radio.checked = true
        } else {
          const input = fieldsRoot.querySelector('[name="' + field.id + '"]')
          if (input) input.value = val
        }
      })
    }

    function setReadonly(readonly) {
      const inputs = fieldsRoot.querySelectorAll("input, select, textarea")
      inputs.forEach((i) => {
        i.disabled = readonly
        if (readonly) i.setAttribute("aria-readonly", "true")
        else i.removeAttribute("aria-readonly")
      })
      submitBtn.classList.toggle("hidden", readonly)
      retakeBtn.classList.toggle("hidden", !readonly)
    }

    function readValues() {
      const data = {}
      fields.forEach((field) => {
        if (field.type === "multiple_choice" && field.display === "checkbox") {
          const checks = fieldsRoot.querySelectorAll('input[name="' + field.id + '"]:checked')
          data[field.id] = Array.prototype.map.call(checks, (c) => c.value)
        } else if (field.type === "multiple_choice" && field.display === "radio") {
          const picked = fieldsRoot.querySelector('input[name="' + field.id + '"]:checked')
          data[field.id] = picked ? picked.value : ""
        } else if (field.type === "number" || field.type === "range") {
          const n = fieldsRoot.querySelector('[name="' + field.id + '"]')
          data[field.id] = n && n.value !== "" ? Number(n.value) : ""
        } else {
          const i = fieldsRoot.querySelector('[name="' + field.id + '"]')
          data[field.id] = i ? i.value : ""
        }
      })
      return data
    }

    function showError(msg) {
      errorEl.textContent = msg
      errorEl.classList.remove("hidden")
    }
    function clearError() {
      errorEl.textContent = ""
      errorEl.classList.add("hidden")
    }

    // ---- Conditional logic --------------------------------------------------
    // Each field's optional `condition` ({field, op, value}) controls whether
    // it shows. Mirrors the server-side evaluate-condition operators
    // (equals / not_equals / not_empty / in / greater_than / less_than).
    // Values are compared as strings so number/text mismatches don't fight us.
    function getDependencyRawValue(depFieldId) {
      const radio = fieldsRoot.querySelector('input[name="' + depFieldId + '"][type="radio"]:checked')
      if (radio) return radio.value
      const checks = fieldsRoot.querySelectorAll('input[name="' + depFieldId + '"][type="checkbox"]:checked')
      if (checks.length > 0) return Array.prototype.map.call(checks, (c) => c.value).join(",")
      const input = fieldsRoot.querySelector('[name="' + depFieldId + '"]')
      return input ? input.value || "" : ""
    }

    function evaluateCondition(condition) {
      if (!condition || !condition.field) return true
      const depValue = String(getDependencyRawValue(condition.field))
      const expected = String(condition.value == null ? "" : condition.value)
      switch (condition.op) {
        case "equals":
          return depValue === expected
        case "not_equals":
          return depValue !== expected
        case "not_empty":
          return depValue.trim() !== ""
        case "in": {
          const values = expected.split(",").map((s) => s.trim())
          return values.indexOf(depValue) !== -1
        }
        // greater_than / less_than: numeric comparison. NaN compared to
        // anything is false, so non-numeric dep values keep the field hidden.
        case "greater_than": {
          const gtDep = Number(depValue)
          const gtExp = Number(expected)
          return !isNaN(gtDep) && !isNaN(gtExp) && gtDep > gtExp
        }
        case "less_than": {
          const ltDep = Number(depValue)
          const ltExp = Number(expected)
          return !isNaN(ltDep) && !isNaN(ltExp) && ltDep < ltExp
        }
        default:
          return true
      }
    }

    function applyConditions() {
      fields.forEach((field) => {
        if (!field.condition || !field.condition.field) return
        const wrap = fieldsRoot.querySelector('[data-field-id="' + field.id + '"]')
        if (!wrap) return
        const visible = evaluateCondition(field.condition)
        wrap.style.display = visible ? "" : "none"
        // When hidden, strip `required` so the browser doesn't block submit on
        // an unfillable invisible field; restore it when shown.
        wrap.querySelectorAll("input, select, textarea").forEach((input) => {
          if (visible && field.required) input.setAttribute("required", "required")
          else if (!visible) input.removeAttribute("required")
        })
        // Clear hidden field values so users don't accidentally submit data
        // for a field they can't see.
        if (!visible) {
          wrap.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((i) => {
            i.checked = false
          })
          wrap.querySelectorAll('input:not([type="checkbox"]):not([type="radio"]), select, textarea').forEach((i) => {
            i.value = i.type === "range" ? i.min || "1" : ""
          })
        }
      })
    }

    function wireConditionListeners() {
      const depFieldIds = {}
      fields.forEach((field) => {
        if (field.condition && field.condition.field) depFieldIds[field.condition.field] = true
      })
      Object.keys(depFieldIds).forEach((depFieldId) => {
        fieldsRoot.querySelectorAll('[name="' + depFieldId + '"]').forEach((input) => {
          input.addEventListener("input", applyConditions)
          input.addEventListener("change", applyConditions)
        })
      })
    }

    // Clear any previously-built field DOM so connect() is idempotent across
    // step navigator innerHTML round-trips.
    fieldsRoot.innerHTML = ""
    fields.forEach((field) => {
      fieldsRoot.appendChild(buildField(field))
    })
    // Always preload prior answers when we have them, regardless of submission
    // state. Submitted → readonly with values; mid-Retake (already-submitted
    // toggled back to false) → editable with values so the user can tweak
    // their prior answers instead of starting from scratch.
    if (existingAnswers) preloadValues(existingAnswers)
    if (alreadySubmitted) setReadonly(true)
    applyConditions()
    wireConditionListeners()

    submitBtn.addEventListener("click", function (e) {
      e.preventDefault()
      clearError()
      const invalid = fieldsRoot.querySelector(":invalid")
      if (invalid) {
        invalid.reportValidity()
        return
      }
      const data = readValues()
      const url = container.getAttribute("data-form-submit-url")
      submitBtn.disabled = true
      fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json", "Accept": "application/json"},
        body: JSON.stringify({data: data})
      })
        .then((r) => r.json().then((j) => ({ok: r.ok, body: j})))
        .then((res) => {
          if (res.ok && res.body && res.body.success) {
            // Persist post-submit state on the container so a navigate-away /
            // navigate-back (which serializes outerHTML and re-runs connect)
            // can rebuild the form in submitted+populated state instead of
            // empty+editable. Without this the next connect reads the stale
            // server-rendered data-form-already-submitted="false" and the user
            // sees blank fields with a Retake button.
            container.setAttribute("data-form-already-submitted", "true")
            container.setAttribute("data-form-existing-answers", JSON.stringify(data))
            setReadonly(true)
            submitBtn.disabled = false
            if (typeof window.BanzaiStepNavigator_goToNextStep === "function") {
              window.BanzaiStepNavigator_goToNextStep()
            }
          } else {
            submitBtn.disabled = false
            showError((res.body && res.body.message) || "Could not save your responses. Please try again.")
          }
        })
        .catch((err) => {
          submitBtn.disabled = false
          showError("Network error. Please try again.")
          console.error(err)
        })
    })

    retakeBtn.addEventListener("click", function (e) {
      e.preventDefault()
      clearError()
      // Pure UI toggle — no server call. The user's existing answers stay in
      // the inputs (and in data-form-existing-answers) so they can edit
      // selectively. The actual DB write happens when they Submit, which
      // upserts the existing row rather than creating a new one.
      container.setAttribute("data-form-already-submitted", "false")
      setReadonly(false)
      // Un-mark the form step in the navigator so the user can't click
      // through to Finish without re-submitting. The navigator also
      // re-disables the finish step if any required step is now incomplete.
      if (typeof window.BanzaiStepNavigator_markStepIncomplete === "function") {
        window.BanzaiStepNavigator_markStepIncomplete("form")
      }
    })
  }
}
